/**
 * Encode and decode condition trees for URL storage.
 *
 * Two wire formats coexist: LZ-String compressed (prefixed with `lz-`) and the
 * legacy Base64 form that predates it. Decoding accepts either; which one to
 * encode with depends on who reads the result — see `queryEncode`.
 *
 * Ported from ~/code/ui/app/helpers/query.ts.
 */

import LZString from 'lz-string';

type QueryValue = Record<string, unknown> | Record<string, unknown>[];

/**
 * Encode conditions with LZ-String compression, producing shorter URLs than
 * Base64. Prefer this for params only this app reads.
 *
 * @example
 * lzQueryEncode({ and: [{ field: 'email', operator: 'exists' }] }); // 'lz-…'
 */
export function lzQueryEncode(value: QueryValue): string {
  return `lz-${LZString.compressToEncodedURIComponent(JSON.stringify(value))}`;
}

/**
 * Decode a Base64-encoded conditions string. Legacy format, used before Feb
 * 2025; prefer `lzQueryDecode`, which accepts both formats.
 */
export function queryDecode(value: string): QueryValue {
  const rawJSON = atob(value);
  let decodedJSON: string;

  try {
    decodedJSON = decodeURIComponent(rawJSON);
  } catch {
    // The back end does not URI-encode its JSON; use it as-is.
    decodedJSON = rawJSON;
  }

  return JSON.parse(decodedJSON);
}

/**
 * Encode conditions as Base64 — the legacy format. Still required wherever the
 * encoded string is handed to an API or read by the Ember app, neither of which
 * understands the `lz-` prefix. Otherwise prefer `lzQueryEncode`.
 */
export function queryEncode(value: QueryValue): string {
  return btoa(encodeURIComponent(JSON.stringify(value)));
}

/**
 * Decode a conditions string in either format, detecting LZ-String by its
 * `lz-` prefix and falling back to Base64.
 *
 * @throws if the value is neither decodable nor valid JSON.
 */
export function lzQueryDecode(value: string): QueryValue {
  if (!value.startsWith('lz-')) {
    return queryDecode(value);
  }

  const decompressed = LZString.decompressFromEncodedURIComponent(value.slice(3));
  if (decompressed == null) {
    throw new Error('Failed to decompress LZ-String encoded conditions');
  }
  return JSON.parse(decompressed);
}

/** `lzQueryDecode` for untrusted input: returns an empty array on failure. */
export function safeQueryDecode(value: string): QueryValue {
  try {
    return lzQueryDecode(value) ?? [];
  } catch {
    return [];
  }
}
